import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import {
  fetchProjectDefaultViewRequest,
  fetchRecentSavedViewsRequest,
  fetchSavedViewRequest,
  fetchSavedViewsInfiniteScrollRequest,
} from '../actions';
import { useRecentlyVisitedViews } from '../components/SavedViews/hooks/useRecentlyVisitedViews';
import { convertQueryParamsToString, getViewIdsQueryString } from '../utils';
import { ViewContext } from './ViewContext';

export const ViewContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const [refetchView, setRefetchView] = useState(false);
  const [resetView, setResetView] = useState(false);
  const [refetchDropdownViews, setRefetchDropdownViews] = useState(false);

  // Boolean toggles
  const toggleRefetchView = () => {
    setRefetchView(!refetchView);
  };
  const toggleResetView = () => {
    setResetView(!resetView);
  };
  const toggleRefetchDropdownViews = () => {
    setRefetchDropdownViews(!refetchDropdownViews);
  };

  // Existing saved view
  const { viewId } = params as any;

  /**
   * Fetch currently selected view
   * refetchView allows for fetchSavedViewRequest to be called in cases where
   * viewId may not have changed (e.g. reselecting the current view and discarding changes)
   */
  useEffect(() => {
    if (viewId) {
      dispatch(fetchSavedViewRequest(viewId));
    }
  }, [viewId, refetchView]);

  // Fetch data for views dropdown
  const recentlyViewedIds = useRecentlyVisitedViews(viewId) || [];

  useEffect(() => {
    if (recentlyViewedIds?.length)
      dispatch(fetchRecentSavedViewsRequest(getViewIdsQueryString(recentlyViewedIds)));
    const queryParams = {
      page: 1,
      pageSize: 25,
    };
    const queryString = convertQueryParamsToString(queryParams);
    dispatch(fetchSavedViewsInfiniteScrollRequest(queryString));
    dispatch(fetchProjectDefaultViewRequest());
  }, [refetchDropdownViews, JSON.stringify(recentlyViewedIds)]);

  return (
    <ViewContext.Provider
      value={{
        viewId,
        refetchView,
        toggleRefetchView,
        refetchDropdownViews,
        toggleRefetchDropdownViews,
        recentlyViewedIds,
        resetView,
        toggleResetView,
      }}
    >
      {children}
    </ViewContext.Provider>
  );
};
